import React, {useState} from 'react';
import Page from "components/Page";
import Card from "components/Card";
import StatList from "components/StatList";
import StatBox from "components/StatBox";
import {CursorClickIcon, InboxInIcon, MailIcon, MailOpenIcon} from "@heroicons/react/outline";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import {dateString, defaultFilters} from 'services/insight';
import InsightControls from "components/InsightControls";
import RoleSelect from "components/InsightControls/RoleSelect";
import HasOfferSelect from "components/InsightControls/HasOfferSelect";
import ChannelSelect from "components/InsightControls/ChannelSelect";
import MessageCopiesSentChart from "components/Chart/MessageCopiesSentChart";
import MessageCopiesViewedChart from "components/Chart/MessageCopiesViewedChart";
import MessageCopiesActionedChart from "components/Chart/MessageCopiesActionedChart";

import type {InsightFilters, MarketingInsightFilters} from "types/OperatorInsights";
import type {Role} from "types/Role";
import type MessageChannel from "types/MessageChannel";
import ExportButton from "../../../components/ExportButton";

// Helpers ----

const breadcrumbs = {"Performance": "/", "Campaigns": "/insights/marketing"}

function toPercentage(rate?: number): string {
  return (100 * Number(rate)).toFixed(1) + '%';
}

// Component ----

function MarketingInsights() {
  const [filters, setFilters] = useState<MarketingInsightFilters>({...defaultFilters, role: null});

  const {data, isLoading, isFetching} = useQuery(
    ['insights', 'marketing', filters],
    ({signal}) => ngage.insights.marketing(filters, {signal}),
    {keepPreviousData: true}
  )

  function changeStandardFilters(standardFilters: InsightFilters) {
    setFilters({...filters, ...standardFilters})
  }

  function selectChannel(channel: MessageChannel | null) {
    setFilters({...filters, channel})
  }

  function selectHasOffer(hasOffer: boolean | null) {
    setFilters({...filters, hasOffer})
  }

  function selectRole(role: Role | null) {
    setFilters({...filters, role: role?.id ?? null})
  }

  const sinceDateString = filters.since.toFormat('dd/MM/yyyy');
  const untilDateString = filters.until.toFormat('dd/MM/yyyy');

  const actions = (
    <ExportButton
      getData={() => ngage.insights.marketingExport(filters, 'csv')}
      dataType={'text/csv'}
      prefix={'ngage-marketing-insights'}
      extension={'csv'}
    />
  );

  return (
    <Page
      title="Campaigns"
      subTitle='Understand more about how app users are engaging with push notifications and emails'
      breadcrumbs={breadcrumbs}
      actions={actions}
      isLoading={isLoading}>

      {/* Controls */}
      <Card isLoading={isFetching}>
        <InsightControls filters={filters} onChange={changeStandardFilters}>

          {/* Context-specific controls */}
          <ChannelSelect id="select-channel" name="channel" label="Channel" onChange={selectChannel}/>
          <HasOfferSelect id="select-has-offer" name="has_offer" label="Has Offer" onChange={selectHasOffer}/>
          <RoleSelect id="select-role" name="role" label="Role" onChange={selectRole}/>
        </InsightControls>
      </Card>

      {/* Stats */}
      <div className="mt-4">
        <StatList columns={4}>
          <StatBox
            name="Messages"
            icon={MailIcon}
            stat={data?.summary.total_messages.toLocaleString()}
            change={data?.summary.new_messages.toLocaleString()}
            isLoading={isLoading}
            tooltip={`The number of messages as of ${untilDateString}. Also shown is the change since ${sinceDateString}.`}
          />

          <StatBox
            name="Message Receipients"
            icon={InboxInIcon}
            stat={data?.summary.total_message_copies.toLocaleString()}
            change={data?.summary.new_message_copies.toLocaleString()}
            isLoading={isLoading}
            tooltip={`The number of people that received messages as of ${untilDateString}. Also shown is the change since ${sinceDateString}.`}
          />

          <StatBox
            name="View Rate"
            icon={MailOpenIcon}
            stat={toPercentage(data?.summary.message_view_rate)}
            isLoading={isLoading}
            tooltip={`The mean average open rate for messages, between ${sinceDateString} and ${untilDateString}.`}
          />

          <StatBox
            name="Action Rate"
            icon={CursorClickIcon}
            stat={toPercentage(data?.summary.message_action_rate)}
            isLoading={isLoading}
            tooltip={`The mean average action rate for messages, between ${sinceDateString} and ${untilDateString}.`}
          />
        </StatList>
      </div>

      {/* Charts */}
      <div className="mt-4 grid gap-4 grid-cols-1 lg:grid-cols-2">

        <Card
          heading="Message Copies Sent"
          subheading={`Total message copies sent per ${filters.interval} between ${dateString(filters.since, filters.interval)} and ${dateString(filters.until, filters.interval)}`}
        >
          {data && <MessageCopiesSentChart data={data} interval={filters.interval} compare={filters.compare}/>}
        </Card>

        <Card
          heading="Message Copies Viewed"
          subheading={`Total message copies viewed per ${filters.interval} between ${dateString(filters.since, filters.interval)} and ${dateString(filters.until, filters.interval)}`}
        >
          {data && <MessageCopiesViewedChart data={data} interval={filters.interval} compare={filters.compare}/>}
        </Card>

        <Card
          heading="Message Copies Actioned"
          subheading={`Total message copies pressed or clicked per ${filters.interval} between ${dateString(filters.since, filters.interval)} and ${dateString(filters.until, filters.interval)}`}
        >
          {data && <MessageCopiesActionedChart data={data} interval={filters.interval} compare={filters.compare}/>}
        </Card>
      </div>
    </Page>
  );
}

export default MarketingInsights;